/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.com/docs/use-static-query/
 */

import * as React from "react"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"

import { INSTAGRAM_URL, BIGHEAD_URL, SHOP } from "../../constants"

const OGP_IMAGE_PATH = "/ogp.jpg"
const OGP_IMAGE_WIDTH = 1200
const OGP_IMAGE_HEIGHT = 630

function Seo({ description, lang, meta, title, pathname }) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            siteUrl
          }
        }
      }
    `
  )

  const metaDescription = description || site.siteMetadata.description
  const defaultTitle = site.siteMetadata?.title
  const siteUrl = site.siteMetadata.siteUrl
  const canonicalUrl = pathname ? `${siteUrl}${pathname}` : null
  const ogpImageUrl = `${siteUrl}${OGP_IMAGE_PATH}`

  // 地域ビジネス(美容室)としての構造化データ。
  // Googleのローカル検索結果・リッチリザルトに店舗情報を提供する。
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "HairSalon",
    name: SHOP.name,
    alternateName: SHOP.brand,
    description: site.siteMetadata.description,
    url: siteUrl,
    image: ogpImageUrl,
    telephone: SHOP.internationalTelephone,
    address: {
      "@type": "PostalAddress",
      postalCode: SHOP.postalCode,
      addressRegion: SHOP.addressRegion,
      addressLocality: SHOP.addressLocality,
      streetAddress: SHOP.streetAddress,
      addressCountry: "JP",
    },
    // 定休日: 火曜/第3水曜。第3水曜のような不定期休はschema.orgで表現できないため
    // 水曜は営業日として含める(詳細はサイト内の表記を参照)
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ],
        opens: SHOP.opens,
        closes: SHOP.closes,
      },
    ],
    priceRange: "¥¥",
    sameAs: [INSTAGRAM_URL, BIGHEAD_URL],
  }

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={title}
      titleTemplate={defaultTitle ? `%s | ${defaultTitle}` : null}
      link={canonicalUrl ? [{ rel: `canonical`, href: canonicalUrl }] : []}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          property: `og:title`,
          content: title,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          property: `og:site_name`,
          content: defaultTitle,
        },
        {
          property: `og:locale`,
          content: `ja_JP`,
        },
        {
          property: `og:image`,
          content: ogpImageUrl,
        },
        {
          property: `og:image:width`,
          content: `${OGP_IMAGE_WIDTH}`,
        },
        {
          property: `og:image:height`,
          content: `${OGP_IMAGE_HEIGHT}`,
        },
        ...(canonicalUrl
          ? [
              {
                property: `og:url`,
                content: canonicalUrl,
              },
            ]
          : []),
        {
          name: `twitter:card`,
          content: `summary_large_image`,
        },
        {
          name: `twitter:creator`,
          content: site.siteMetadata?.author || ``,
        },
        {
          name: `twitter:title`,
          content: title,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
        {
          name: `twitter:image`,
          content: ogpImageUrl,
        },
      ].concat(meta)}
    >
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  )
}

Seo.defaultProps = {
  lang: `ja`,
  meta: [],
  description: ``,
  pathname: null,
}

Seo.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  pathname: PropTypes.string,
}

export default Seo
